import React, { useMemo } from 'react'
import { useAuthContext } from '../../context/auth.context'
import { ROLE_SYS, useRoleContext } from '../role.context'

// `fallback` is what to show instead once the sys permissions have loaded
// and the person lacks the access asked for (a gated route's NoAccess view).
// Until they load, nothing is shown, so the fallback never flashes.
export const RoleSys = ({ children, type, accessRead, accessCreate, accessUpdate, accessDelete, fallback }) => {
  const authContext = useAuthContext()
  const roleContext = useRoleContext()

  let display = useMemo(
    () => {
      if (authContext.state.isSysRoot) {
        return true
      }

      if (!roleContext.sysRoles[type]) {
        return false
      }

      if (accessRead) return roleContext.sysRoles[type].accessRead
      else if (accessCreate) return roleContext.sysRoles[type].accessCreate
      else if (accessUpdate) return roleContext.sysRoles[type].accessUpdate
      else if (accessDelete) return roleContext.sysRoles[type].accessDelete
      else return false
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authContext.state, roleContext.sysRoles]
  )

  if (!display && fallback && !authContext.state.isSysRoot && roleContext.sysLoaded) {
    return fallback
  }

  return (
    <React.Fragment>
      {display && children}
    </React.Fragment>
  )
}

export const RoleSysDashboard = ({ children, accessRead, accessCreate, accessUpdate, accessDelete }) => {
  return (
    <RoleSys type={ROLE_SYS.SYS_DASHBOARD} accessRead={accessRead} accessCreate={accessCreate} accessUpdate={accessUpdate} accessDelete={accessDelete}>
      {children}
    </RoleSys>
  )
}

export const RoleSysApp = ({ children, accessRead, accessCreate, accessUpdate, accessDelete }) => {
  return (
    <RoleSys type={ROLE_SYS.SYS_APP} accessRead={accessRead} accessCreate={accessCreate} accessUpdate={accessUpdate} accessDelete={accessDelete}>
      {children}
    </RoleSys>
  )
}

export const RoleSysOrganization = ({ children, accessRead, accessCreate, accessUpdate, accessDelete }) => {
  return (
    <RoleSys type={ROLE_SYS.SYS_ORGANIZATION} accessRead={accessRead} accessCreate={accessCreate} accessUpdate={accessUpdate} accessDelete={accessDelete}>
      {children}
    </RoleSys>
  )
}

export const RoleSysLogin = ({ children, accessRead, accessCreate, accessUpdate, accessDelete }) => {
  return (
    <RoleSys type={ROLE_SYS.SYS_LOGIN} accessRead={accessRead} accessCreate={accessCreate} accessUpdate={accessUpdate} accessDelete={accessDelete}>
      {children}
    </RoleSys>
  )
}

export const RoleSysAuthStatus = ({ children, accessRead, accessCreate, accessUpdate, accessDelete }) => {
  return (
    <RoleSys type={ROLE_SYS.SYS_AUTH_STATUS} accessRead={accessRead} accessCreate={accessCreate} accessUpdate={accessUpdate} accessDelete={accessDelete}>
      {children}
    </RoleSys>
  )
}

export const RoleSysProvider = ({ children, accessRead, accessCreate, accessUpdate, accessDelete }) => {
  return (
    <RoleSys type={ROLE_SYS.SYS_PROVIDER} accessRead={accessRead} accessCreate={accessCreate} accessUpdate={accessUpdate} accessDelete={accessDelete}>
      {children}
    </RoleSys>
  )
}

export const RoleSysSendgridEvent = ({ children, accessRead, accessCreate, accessUpdate, accessDelete, fallback }) => {
  return (
    <RoleSys type={ROLE_SYS.SYS_SENDGRID_EVENT} accessRead={accessRead} accessCreate={accessCreate} accessUpdate={accessUpdate} accessDelete={accessDelete} fallback={fallback}>
      {children}
    </RoleSys>
  )
}

export const RoleSysRequestLog = ({ children, accessRead, accessCreate, accessUpdate, accessDelete, fallback }) => {
  return (
    <RoleSys type={ROLE_SYS.SYS_REQUEST_LOG} accessRead={accessRead} accessCreate={accessCreate} accessUpdate={accessUpdate} accessDelete={accessDelete} fallback={fallback}>
      {children}
    </RoleSys>
  )
}

export const RoleSysErrors = ({ children, accessRead, accessCreate, accessUpdate, accessDelete, fallback }) => {
  return (
    <RoleSys type={ROLE_SYS.SYS_ERRORS} accessRead={accessRead} accessCreate={accessCreate} accessUpdate={accessUpdate} accessDelete={accessDelete} fallback={fallback}>
      {children}
    </RoleSys>
  )
}

export const RoleSysAlertRouting = ({ children, accessRead, accessCreate, accessUpdate, accessDelete, fallback }) => {
  return (
    <RoleSys type={ROLE_SYS.SYS_ALERT_ROUTING} accessRead={accessRead} accessCreate={accessCreate} accessUpdate={accessUpdate} accessDelete={accessDelete} fallback={fallback}>
      {children}
    </RoleSys>
  )
}

export const RoleSysApiKey = ({ children, accessRead, accessCreate, accessUpdate, accessDelete, fallback }) => {
  return (
    <RoleSys type={ROLE_SYS.SYS_API_KEY} accessRead={accessRead} accessCreate={accessCreate} accessUpdate={accessUpdate} accessDelete={accessDelete} fallback={fallback}>
      {children}
    </RoleSys>
  )
}

export const RoleSysLmsPreset = ({ children, accessRead, accessCreate, accessUpdate, accessDelete, fallback }) => {
  return (
    <RoleSys type={ROLE_SYS.SYS_LMS_PRESET} accessRead={accessRead} accessCreate={accessCreate} accessUpdate={accessUpdate} accessDelete={accessDelete} fallback={fallback}>
      {children}
    </RoleSys>
  )
}

export const RoleSysAuditLog = ({ children, accessRead, accessCreate, accessUpdate, accessDelete, fallback }) => {
  return (
    <RoleSys type={ROLE_SYS.SYS_AUDIT_LOG} accessRead={accessRead} accessCreate={accessCreate} accessUpdate={accessUpdate} accessDelete={accessDelete} fallback={fallback}>
      {children}
    </RoleSys>
  )
}

export const RoleSysAuditRetention = ({ children, accessRead, accessCreate, accessUpdate, accessDelete, fallback }) => {
  return (
    <RoleSys type={ROLE_SYS.SYS_AUDIT_RETENTION} accessRead={accessRead} accessCreate={accessCreate} accessUpdate={accessUpdate} accessDelete={accessDelete} fallback={fallback}>
      {children}
    </RoleSys>
  )
}

export const RoleSysNotify = ({ children, accessRead, accessCreate, accessUpdate, accessDelete, fallback }) => {
  return (
    <RoleSys type={ROLE_SYS.SYS_NOTIFY} accessRead={accessRead} accessCreate={accessCreate} accessUpdate={accessUpdate} accessDelete={accessDelete} fallback={fallback}>
      {children}
    </RoleSys>
  )
}

export const RoleSysAiToolsTelemetry = ({ children, accessRead, accessCreate, accessUpdate, accessDelete, fallback }) => {
  return (
    <RoleSys type={ROLE_SYS.SYS_AI_TOOLS_TELEMETRY} accessRead={accessRead} accessCreate={accessCreate} accessUpdate={accessUpdate} accessDelete={accessDelete} fallback={fallback}>
      {children}
    </RoleSys>
  )
}

export const RoleSysPackages = ({ children, accessRead, accessCreate, accessUpdate, accessDelete, fallback }) => {
  return (
    <RoleSys type={ROLE_SYS.SYS_PACKAGES} accessRead={accessRead} accessCreate={accessCreate} accessUpdate={accessUpdate} accessDelete={accessDelete} fallback={fallback}>
      {children}
    </RoleSys>
  )
}

export const RoleSysBilling = ({ children, accessRead, accessCreate, accessUpdate, accessDelete, fallback }) => {
  return (
    <RoleSys type={ROLE_SYS.SYS_BILLING} accessRead={accessRead} accessCreate={accessCreate} accessUpdate={accessUpdate} accessDelete={accessDelete} fallback={fallback}>
      {children}
    </RoleSys>
  )
}

export const RoleSysConsentLog = ({ children, accessRead, accessCreate, accessUpdate, accessDelete, fallback }) => {
  return (
    <RoleSys type={ROLE_SYS.SYS_CONSENT_LOG} accessRead={accessRead} accessCreate={accessCreate} accessUpdate={accessUpdate} accessDelete={accessDelete} fallback={fallback}>
      {children}
    </RoleSys>
  )
}
